import { RootState } from '@/components/WellLoggingOperations/types';
import { Button, Drawer } from 'antd';
import { DrawerProps } from 'antd/lib';
import { FC, ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { GeoUserProfile, ProfileWell, ProfileWellExtended } from '../types';
import { groupWellsToTree, toPermission } from '../utils';
import { PERMISSION_TITLES, WellParsedInfo } from '@/store/geo-test/types';
import classNames from 'classnames';
import DropdownWrapper from '@/shared/DropDownWrapper';
import type { Item as DropdownItem } from '@/shared/DropDownWrapper/types';
import { MoreOutlined } from '@ant-design/icons';
import Cookies from 'js-cookie';
import { useDispatch } from 'react-redux';
import { MessageInstance } from 'antd/es/message/interface';

type Info = {
    title?: string;
    content: string;
};

export const getWellInfo = (well: ProfileWell): WellParsedInfo => {
    const attributes = JSON.parse(well.attributes) as WellParsedInfo;
    return attributes;
};

const Section: FC<{ title: string; info: Info[]; className?: string; dropdown?: ReactNode }> = ({
    title,
    info,
    className,
    dropdown,
}) => {
    return (
        <div className={classNames('flex flex-col mt-4', className)}>
            {!dropdown ? (
                <span className="leading-6">{title}</span>
            ) : (
                <div className="flex justify-between">
                    <span className="leading-6">{title}</span>
                    {dropdown}
                </div>
            )}
            {info.map(({ title, content }) => (
                <span className="leading-6 font-normal">{title ? `${title}: ${content}` : content}</span>
            ))}
        </div>
    );
};

const WellSections: FC<{ wells: ProfileWellExtended[] }> = ({ wells }) => {
    const wellsTree = groupWellsToTree(wells);
    return wellsTree.map(({ fieldName, elevations }) => (
        <div className="flex flex-col mt-4">
            <span className="leading-6">{fieldName}</span>
            {elevations.map(({ kupolName, wells }) => (
                <Section
                    className="!mt-1"
                    title={kupolName}
                    info={wells.map(({ wellNumber }) => ({ content: wellNumber }))}
                />
            ))}
        </div>
    ));
};

const ProfileInfo: FC<
    GeoUserProfile & { messageApi: MessageInstance; onClose: (e: React.MouseEvent | React.KeyboardEvent) => void }
> = ({ id, profileOwner, isPrivate, readOnly, wells, gasHorizon, waterHorizon, versionName, messageApi, onClose }) => {
    const dispatch = useDispatch();
    const { deleteProfile, changeProfile } = dispatch.geoTest;
    const permission = toPermission(isPrivate, readOnly);
    const generalInfo: Info[] = [
        {
            title: 'Автор',
            content: profileOwner,
        },
        {
            title: 'Доступ',
            content: PERMISSION_TITLES[permission],
        },
    ];
    const paramsInfo: Info[] = [
        {
            title: 'ГНК',
            content: gasHorizon?.toString() ?? '-',
        },
        {
            title: 'ВНК',
            content: waterHorizon?.toString() ?? '-',
        },
        {
            title: 'РИГИС',
            content: versionName,
        },
    ];

    const dropdownItems: DropdownItem[] = [
        {
            type: undefined,
            title: 'Сохранить изменения',
            itemKey: 'change',
            onClick: () => changeProfile({ id, messageApi }),
        },
        {
            type: 'divider',
        },
        {
            type: undefined,
            title: 'Удалить',
            itemKey: 'delete',
            onClick: async () => {
                await deleteProfile({ id, messageApi });
                onClose?.({} as React.MouseEvent<HTMLButtonElement>);
            },
            antProps: {
                danger: true,
            },
        },
    ];

    const currentUsername = Cookies.get('username') as string;
    const isOwner = profileOwner === currentUsername;

    const dropdown = (
        <DropdownWrapper trigger={['click']} dropdownItems={dropdownItems}>
            <Button type="text" className="text-base" icon={<MoreOutlined />} />
        </DropdownWrapper>
    );
    return (
        <div className="p-4 pt-0 text-xs">
            <Section title={`Профиль ${profileOwner}`} info={generalInfo} dropdown={isOwner ? dropdown : undefined} />
            <Section title="Параметры" info={paramsInfo} />
            <WellSections wells={wells} />
        </div>
    );
};

export const ProfileInfoDrawer: FC<DrawerProps & { selectedId: number | null; messageApi: MessageInstance }> = ({
    open,
    onClose,
    afterOpenChange,
    selectedId,
    messageApi,
}) => {
    const { userProfiles } = useSelector((state: RootState) => state.geoTest);
    const profile = userProfiles.find(({ id }) => selectedId === id);

    return (
        <Drawer
            placement="right"
            width={300}
            closable={false}
            onClose={onClose}
            afterOpenChange={afterOpenChange}
            open={open}
            getContainer={false}
            destroyOnHidden
            rootStyle={{ position: 'absolute' }}
            styles={{ body: { padding: 0 }, mask: { background: 'none' } }}
        >
            {selectedId && profile && <ProfileInfo {...profile} messageApi={messageApi} onClose={onClose!} />}
        </Drawer>
    );
};
